import React, { useMemo } from 'react';
import type { Driver } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { useVehicleDrawerStore } from '@hero/hooks-shared';
import { DrawerContentCard } from '../../../app/Drawer/DrawerContentCard';
import { Badge } from '../../../ui/Badge';
import { EyeIcon } from '@hero/web-icons';
import { FileThumbnail } from '../../../app/File/shared/FileThumbnail';
import { formatPlateForDisplay } from '../../../app/Select/SelectAsync/selectorEntityRowUtils';

export interface DriverSummaryTabProps {
  driver: Driver;
}

function imageSrc(img: { url?: string; upload?: string } | undefined): string {
  if (!img) return '';
  return (img.url ?? img.upload ?? '').trim();
}

export function DriverSummaryTab({ driver }: DriverSummaryTabProps) {
  const { t } = useTranslation(['m-driver', 'c-nationalities']);
  const openVehicleDrawer = useVehicleDrawerStore(s => s.open);

  const { nationalIdSrc, licenseSrc } = useMemo(() => {
    const legal = driver.legal;
    return {
      nationalIdSrc: imageSrc(legal?.idImage),
      licenseSrc: imageSrc(legal?.driverLicenseImage),
    };
  }, [driver]);

  const showDocuments = !!(nationalIdSrc || licenseSrc);

  const vehicleInfo = driver.vehicle;
  const vehicleId = vehicleInfo?._id;

  const plateDisplay = vehicleInfo?.plate
    ? formatPlateForDisplay(vehicleInfo.plate) || vehicleInfo.plate
    : '';

  const vehicleCard =
    vehicleInfo && vehicleId ? (
      <button
        type="button"
        className="flex items-center gap-2 text-primary hover:underline"
        onClick={() => openVehicleDrawer(String(vehicleId))}>
        <span dir="auto">{plateDisplay || t('drawer.fields.vehicle')}</span>
        <EyeIcon className="h-3.5 w-3.5" />
      </button>
    ) : (
      <span className="text-muted-foreground">{t('drawer.no_vehicle_assigned')}</span>
    );

  return (
    <div className="flex flex-col gap-4 p-5">
      <DrawerContentCard
        title={t('drawer.tabs.summary')}
        list={[
          { label: t('drawer.fields.name'), value: driver.name || '-' },
          { label: t('drawer.fields.phone'), value: driver.phone || '-' },
          {
            label: t('drawer.fields.status'),
            value: (
              <Badge
                variant="secondary"
                className={
                  driver.status === 'active'
                    ? 'bg-success-50 text-success-400'
                    : 'bg-gray-100 text-gray-600'
                }>
                {driver.status === 'active' ? t('status.available') : t('status.offline')}
              </Badge>
            ),
          },
          {
            label: t('drawer.fields.driver_id'),
            value: driver.legal?.id || '-',
            hidden: !driver.legal?.id,
          },
          {
            label: t('drawer.fields.nationality'),
            value: driver.legal?.nationality || '-',
            hidden: !driver.legal?.nationality,
          },
        ]}
      />

      <DrawerContentCard
        title={t('drawer.fields.vehicle')}
        list={[{ label: t('drawer.fields.vehicle'), value: vehicleCard }]}
      />

      {showDocuments && (
        <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-4">
          <div className="flex flex-wrap items-start gap-6">
            {nationalIdSrc ? (
              <div className="flex min-w-0 flex-col gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('drawer.fields.national_id')}
                </span>
                <FileThumbnail
                  src={nationalIdSrc}
                  size="xl"
                  expandable
                  className="max-w-[280px] rounded-lg"
                />
              </div>
            ) : null}
            {licenseSrc ? (
              <div className="flex min-w-0 flex-col gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('drawer.fields.license')}
                </span>
                <FileThumbnail
                  src={licenseSrc}
                  size="xl"
                  expandable
                  className="max-w-[280px] rounded-lg"
                />
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
